import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Card,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Button,
  Alert,
  Table,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Badge
} from '@mui/joy';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import axiosInstance from '../util/axiosConfig';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import ResponsiveDetailTable from '../components/ResponsiveDetailTable';
import ReportMobileRowList from '../components/ReportMobileRowList';
import { BRAND } from '../brand';

const COLORS = [BRAND.colors.navy, '#2f77d0', '#2e7d32', '#6a1b9a', '#d32f2f', '#00838f'];

const todayStr = () => new Date().toISOString().slice(0, 10);
const startOfYearStr = () => `${new Date().getFullYear()}-01-01`;

const fmtMoney = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const GROUP_LABELS = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

const fmtDateShort = (value) => {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS_SHORT[d.getMonth()];
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const fmtDateTime = (value) => {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = MONTHS_SHORT[d.getMonth()];
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const hh = String(hours).padStart(2, '0');
  return `${day}-${month}-${year} ${hh}:${minutes} ${ampm}`;
};

const daysToExpire = (expiryDateStr, asOfStr) => {
  if (!expiryDateStr || !asOfStr) return '';
  const d1 = new Date(`${String(asOfStr).slice(0, 10)}T00:00:00`);
  const d2 = new Date(`${String(expiryDateStr).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return '';
  const diffMs = d2.getTime() - d1.getTime();
  return Math.round(diffMs / (24 * 60 * 60 * 1000));
};

const Reports = () => {
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  const [from, setFrom] = useState(startOfYearStr());
  const [to, setTo] = useState(todayStr());
  const [group, setGroup] = useState('month');
  const [vendorId, setVendorId] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [consumption, setConsumption] = useState(null);
  const [stockReductions, setStockReductions] = useState(null);
  const [stockReductionsError, setStockReductionsError] = useState('');
  const [purchases, setPurchases] = useState(null);
  const [expiry, setExpiry] = useState(null);
  const [windowDays, setWindowDays] = useState('30');

  const [drillOpen, setDrillOpen] = useState(false);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState('');
  const [drillPeriod, setDrillPeriod] = useState('');
  const [drillData, setDrillData] = useState(null);
  const [drillTab, setDrillTab] = useState('materials');

  const [srDrillOpen, setSrDrillOpen] = useState(false);
  const [srDrillLoading, setSrDrillLoading] = useState(false);
  const [srDrillError, setSrDrillError] = useState('');
  const [srDrillPeriod, setSrDrillPeriod] = useState('');
  const [srDrillData, setSrDrillData] = useState(null);
  const [srDrillTab, setSrDrillTab] = useState('materials');

  const [pDrillOpen, setPDrillOpen] = useState(false);
  const [pDrillLoading, setPDrillLoading] = useState(false);
  const [pDrillError, setPDrillError] = useState('');
  const [pDrillPeriod, setPDrillPeriod] = useState('');
  const [pDrillData, setPDrillData] = useState(null);
  const [pDrillTab, setPDrillTab] = useState('materials');

  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(startOfYearStr());
  const [draftTo, setDraftTo] = useState(todayStr());
  const [draftGroup, setDraftGroup] = useState('month');
  const [draftVendorId, setDraftVendorId] = useState('');
  const [draftWindowDays, setDraftWindowDays] = useState('30');

  const vendorNameById = useMemo(() => {
    const m = new Map();
    for (const v of vendors) m.set(String(v.id), v.name);
    return m;
  }, [vendors]);

  const fetchVendors = async () => {
    try {
      setVendorsLoading(true);
      const res = await axiosInstance.get('/vendors');
      setVendors(res.data.vendors || []);
    } catch (e) {
      // keep silent; vendor select is optional
      setVendors([]);
    } finally {
      setVendorsLoading(false);
    }
  };

  const runReports = async (overrides = {}) => {
    const reportFrom = overrides.from ?? from;
    const reportTo = overrides.to ?? to;
    const reportGroup = overrides.group ?? group;
    const reportVendorId = overrides.vendorId ?? vendorId;
    const reportWindowDays = overrides.windowDays ?? windowDays;

    try {
      setLoading(true);
      setError('');

      const vendorIdParam = reportVendorId ? { vendorId: reportVendorId } : {};
      const reportParams = { from: reportFrom, to: reportTo, group: reportGroup, ...vendorIdParam };
      const [consRes, purRes, expRes] = await Promise.all([
        axiosInstance.get('/reports/consumption', { params: reportParams }),
        axiosInstance.get('/reports/purchases', { params: reportParams }),
        axiosInstance.get('/reports/expiry', { params: { asOf: todayStr(), windowDays: reportWindowDays || '30' } })
      ]);

      setConsumption(consRes.data);
      setPurchases(purRes.data);
      setExpiry(expRes.data);

      if (consRes.data?.stockReductions) {
        setStockReductions(consRes.data.stockReductions);
        setStockReductionsError('');
      } else {
        try {
          const srRes = await axiosInstance.get('/reports/stock-reductions', { params: reportParams });
          setStockReductions(srRes.data);
          setStockReductionsError('');
        } catch (srErr) {
          setStockReductions(null);
          setStockReductionsError(srErr.response?.data?.error || 'Failed to load returns & disposals');
        }
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const openConsumptionDrilldown = async (periodLabel) => {
    if (!periodLabel) return;
    try {
      setDrillOpen(true);
      setDrillLoading(true);
      setDrillError('');
      setDrillPeriod(periodLabel);
      setDrillData(null);
      setDrillTab('materials');

      const params = { group, period: periodLabel, limit: 50 };
      if (vendorId) params.vendorId = vendorId;

      const res = await axiosInstance.get('/reports/consumption/drilldown', { params });
      setDrillData(res.data);
    } catch (e) {
      setDrillError(e.response?.data?.error || 'Failed to load drilldown');
    } finally {
      setDrillLoading(false);
    }
  };

  const openStockReductionsDrilldown = async (periodLabel) => {
    if (!periodLabel) return;
    try {
      setSrDrillOpen(true);
      setSrDrillLoading(true);
      setSrDrillError('');
      setSrDrillPeriod(periodLabel);
      setSrDrillData(null);
      setSrDrillTab('materials');

      const params = { group, period: periodLabel, limit: 50 };
      if (vendorId) params.vendorId = vendorId;

      const res = await axiosInstance.get('/reports/stock-reductions/drilldown', { params });
      setSrDrillData(res.data);
    } catch (e) {
      setSrDrillError(e.response?.data?.error || 'Failed to load returns & disposals drilldown');
    } finally {
      setSrDrillLoading(false);
    }
  };

  const openPurchasesDrilldown = async (periodLabel) => {
    if (!periodLabel) return;
    try {
      setPDrillOpen(true);
      setPDrillLoading(true);
      setPDrillError('');
      setPDrillPeriod(periodLabel);
      setPDrillData(null);
      setPDrillTab('materials');

      const params = { group, period: periodLabel, limit: 50 };
      if (vendorId) params.vendorId = vendorId;

      const res = await axiosInstance.get('/reports/purchases/drilldown', { params });
      setPDrillData(res.data);
    } catch (e) {
      setPDrillError(e.response?.data?.error || 'Failed to load purchases drilldown');
    } finally {
      setPDrillLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    runReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFilter = useCallback(() => {
    setDraftFrom(from);
    setDraftTo(to);
    setDraftGroup(group);
    setDraftVendorId(vendorId);
    setDraftWindowDays(windowDays);
    setFilterOpen(true);
  }, [from, to, group, vendorId, windowDays]);

  const applyFilters = async () => {
    setFrom(draftFrom);
    setTo(draftTo);
    setGroup(draftGroup);
    setVendorId(draftVendorId);
    setWindowDays(draftWindowDays);
    setFilterOpen(false);
    await runReports({
      from: draftFrom,
      to: draftTo,
      group: draftGroup,
      vendorId: draftVendorId,
      windowDays: draftWindowDays
    });
  };

  const hasActiveFilters =
    from !== startOfYearStr() ||
    group !== 'month' ||
    Boolean(vendorId) ||
    String(windowDays) !== '30';

  const headerAction = useMemo(
    () => (
      <Badge invisible={!hasActiveFilters} color="primary" variant="solid" size="sm">
        <Button
          variant="outlined"
          color="primary"
          size="sm"
          startDecorator={<FilterListIcon />}
          onClick={openFilter}
        >
          Filter
        </Button>
      </Badge>
    ),
    [hasActiveFilters, openFilter]
  );

  useSetPageHeaderAction(headerAction);

  const vendorBreakdownMerged = useMemo(() => {
    const vb = purchases?.vendorBreakdown;
    if (!vb) return [];
    const merged = new Map();
    const push = (rows, kind) => {
      for (const r of rows || []) {
        const id = r.vendor_id == null ? 'unknown' : String(r.vendor_id);
        const prev = merged.get(id) || {
          vendor_id: r.vendor_id,
          vendor_name: r.vendor_name || 'Unknown',
          materialsAmount: 0,
          disposablesAmount: 0,
          implantsAmount: 0,
          totalAmount: 0
        };
        const amount = Number(r.amount || 0);
        prev[`${kind}Amount`] += amount;
        prev.totalAmount += amount;
        merged.set(id, prev);
      }
    };
    push(vb.materials, 'materials');
    push(vb.disposables, 'disposables');
    push(vb.implants, 'implants');
    return Array.from(merged.values()).sort((a, b) => (b.totalAmount || 0) - (a.totalAmount || 0));
  }, [purchases]);

  const stockReductionSeries = stockReductions?.series || [];
  const hasStockReductionData = stockReductionSeries.some(
    (row) =>
      Number(row.materialsReturned || 0) > 0 ||
      Number(row.materialsDisposed || 0) > 0 ||
      Number(row.implantsReturned || 0) > 0 ||
      Number(row.implantsDisposed || 0) > 0
  );

  const hasExpiryData = useMemo(() => {
    if (!expiry) return false;
    return (
      (expiry.expiringSoon || []).length > 0 ||
      (expiry.expired || []).length > 0 ||
      (expiry.implantsExpiringSoon || []).length > 0 ||
      (expiry.implantsExpired || []).length > 0
    );
  }, [expiry]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip size="sm" variant="soft">
          {from} → {to}
        </Chip>
        <Chip size="sm" variant="soft">
          {GROUP_LABELS[group] || group}
        </Chip>
        {vendorId ? (
          <Chip size="sm" variant="soft" color="primary">
            {vendorNameById.get(vendorId) || vendorId}
          </Chip>
        ) : (
          <Chip size="sm" variant="soft">
            All vendors
          </Chip>
        )}
        <Chip size="sm" variant="soft">
          Expiry: {windowDays} days
        </Chip>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Modal open={filterOpen} onClose={() => setFilterOpen(false)}>
        <ModalDialog sx={{ width: { xs: '95vw', sm: 480 }, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h3" sx={{ color: 'primary.500', mb: 2 }}>
            Report filters
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel>From</FormLabel>
              <Input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} />
            </FormControl>

            <FormControl>
              <FormLabel>To</FormLabel>
              <Input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} />
            </FormControl>

            <FormControl>
              <FormLabel>Group</FormLabel>
              <Select value={draftGroup} onChange={(_, v) => setDraftGroup(v || 'month')}>
                <Option value="day">Daily</Option>
                <Option value="week">Weekly</Option>
                <Option value="month">Monthly</Option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Vendor</FormLabel>
              <Select
                value={draftVendorId || ''}
                onChange={(_, v) => setDraftVendorId(v || '')}
                placeholder={vendorsLoading ? 'Loading vendors...' : 'All vendors'}
                disabled={vendorsLoading}
              >
                <Option value="">All vendors</Option>
                {vendors.map((v) => (
                  <Option key={v.id} value={String(v.id)}>
                    {v.name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Expiry window (days)</FormLabel>
              <Input
                type="number"
                value={draftWindowDays}
                onChange={(e) => setDraftWindowDays(e.target.value)}
                slotProps={{ input: { min: 0, max: 3650 } }}
              />
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
              <Button variant="plain" color="neutral" onClick={() => setFilterOpen(false)}>
                Cancel
              </Button>
              <Button
                loading={loading}
                onClick={applyFilters}
                sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.600' } }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {/* Consumption */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
          <Typography level="h2" sx={{ color: 'primary.500' }}>
            Consumption
          </Typography>
          {consumption?.totals && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip color="warning" variant="soft">Total: {fmtMoney(consumption.totals.total)}</Chip>
              <Chip variant="soft">Materials: {fmtMoney(consumption.totals.materials)}</Chip>
              <Chip variant="soft">Disposables: {fmtMoney(consumption.totals.disposables)}</Chip>
              <Chip variant="soft">Implants: {fmtMoney(consumption.totals.implants)}</Chip>
            </Box>
          )}
        </Box>

        <Box sx={{ height: 320, mt: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={consumption?.series || []}
              margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
              onClick={(state) => {
                const label = state?.activeLabel;
                if (label) openConsumptionDrilldown(label);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="materials" stroke={COLORS[0]} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="disposables" stroke={COLORS[1]} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="implants" stroke={COLORS[2]} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="total" stroke={COLORS[4]} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Typography level="body-xs" sx={{ mt: 1, opacity: 0.75 }}>
          Consumed usage only (excludes returns and disposals). Tip: click a point on the chart to drill down by item.
        </Typography>
      </Card>

      {/* Returns & disposals (materials & implants with expiry) */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
          <Typography level="h2" sx={{ color: 'primary.500' }}>
            Returns &amp; disposals
          </Typography>
          {stockReductions?.totals && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip color="success" variant="soft">Returned: {fmtMoney(stockReductions.totals.totalReturned)}</Chip>
              <Chip color="warning" variant="soft">Disposed: {fmtMoney(stockReductions.totals.totalDisposed)}</Chip>
              <Chip variant="soft">Materials returned: {fmtMoney(stockReductions.totals.materialsReturned)}</Chip>
              <Chip variant="soft">Materials disposed: {fmtMoney(stockReductions.totals.materialsDisposed)}</Chip>
              <Chip variant="soft">Implants returned: {fmtMoney(stockReductions.totals.implantsReturned)}</Chip>
              <Chip variant="soft">Implants disposed: {fmtMoney(stockReductions.totals.implantsDisposed)}</Chip>
            </Box>
          )}
        </Box>

        {stockReductionsError && (
          <Alert color="danger" sx={{ mt: 2 }}>
            {stockReductionsError}
          </Alert>
        )}

        {loading && !stockReductions && !stockReductionsError && (
          <Typography level="body-sm" sx={{ mt: 2, opacity: 0.75 }}>
            Loading returns &amp; disposals…
          </Typography>
        )}

        {!loading && stockReductions && !hasStockReductionData && !stockReductionsError && (
          <Box
            sx={{
              mt: 2,
              minHeight: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px dashed',
              borderColor: 'neutral.300',
              borderRadius: 'sm',
              px: 2
            }}
          >
            <Typography level="body-sm" sx={{ opacity: 0.75, textAlign: 'center' }}>
              No returns or disposals in the selected date range ({from} to {to}).
              Record them from a material or implant batch with an expiry date.
            </Typography>
          </Box>
        )}

        {hasStockReductionData && (
          <Box sx={{ height: 320, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={stockReductionSeries}
                margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                onClick={(state) => {
                  const label = state?.activeLabel;
                  if (label) openStockReductionsDrilldown(label);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="materialsReturned" name="Materials returned" stackId="materials" fill={COLORS[2]} />
                <Bar dataKey="materialsDisposed" name="Materials disposed" stackId="materials" fill={COLORS[4]} />
                <Bar dataKey="implantsReturned" name="Implants returned" stackId="implants" fill={COLORS[0]} />
                <Bar dataKey="implantsDisposed" name="Implants disposed" stackId="implants" fill={COLORS[5]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        <Typography level="body-xs" sx={{ mt: 1, opacity: 0.75 }}>
          Return to vendor or write-off for batches with an expiry date.
          {hasStockReductionData ? ' Tip: click a bar to drill down by item.' : ''}
        </Typography>
      </Card>

      {/* Purchases */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
          <Typography level="h2" sx={{ color: 'primary.500' }}>
            Purchases (Qty & Amount)
          </Typography>
          {purchases?.totals && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip color="warning" variant="soft">Amount: {fmtMoney(purchases.totals.totalAmount)}</Chip>
              <Chip variant="soft">Qty: {fmtMoney(purchases.totals.totalQty)}</Chip>
            </Box>
          )}
        </Box>

        <Box sx={{ height: 320, mt: 2 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={purchases?.series || []}
              margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
              onClick={(state) => {
                const label = state?.activeLabel;
                if (label) openPurchasesDrilldown(label);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="materialsAmount" name="Materials Amount" fill={COLORS[0]} />
              <Bar dataKey="disposablesAmount" name="Disposables Amount" fill={COLORS[1]} />
              <Bar dataKey="implantsAmount" name="Implants Amount" fill={COLORS[2]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Typography level="body-xs" sx={{ mt: 1, opacity: 0.75 }}>
          Tip: click a bar to drill down by item.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 2 }}>
          <Card variant="soft" sx={{ p: 2 }}>
            <Typography level="h3" sx={{ mb: 1 }}>
              Vendor breakdown (Total Amount)
            </Typography>
            <Box sx={{ height: 200, mb: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <Tooltip formatter={(value, name) => [`₹${fmtMoney(value)}`, name]} />
                  <Pie
                    data={(vendorBreakdownMerged || []).slice(0, 8)}
                    dataKey="totalAmount"
                    nameKey="vendor_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={72}
                    labelLine={false}
                  >
                    {(vendorBreakdownMerged || []).slice(0, 8).map((entry, idx) => (
                      <Cell key={entry.vendor_id ?? `k-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box
              sx={{
                maxHeight: 200,
                overflowY: 'auto',
                overflowX: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                mt: 1,
                pt: 1,
                pr: 0.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0,0,0,0.25) transparent',
                '&::-webkit-scrollbar': { width: 8 },
                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.22)',
                  borderRadius: 4
                }
              }}
            >
              {(vendorBreakdownMerged || []).map((entry, idx) => (
                <Box
                  key={entry.vendor_id ?? `legend-${idx}`}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      mt: '4px',
                      flexShrink: 0,
                      borderRadius: '2px',
                      backgroundColor: COLORS[idx % COLORS.length]
                    }}
                  />
                  <Typography level="body-xs" sx={{ flex: 1, lineHeight: 1.35, wordBreak: 'break-word' }}>
                    {entry.vendor_name}
                  </Typography>
                  <Typography level="body-xs" sx={{ flexShrink: 0, fontWeight: 600 }}>
                    ₹{fmtMoney(entry.totalAmount)}
                  </Typography>
                </Box>
              ))}
              {(vendorBreakdownMerged || []).length === 0 && (
                <Typography level="body-xs" sx={{ opacity: 0.7 }}>
                  No purchases found for this range.
                </Typography>
              )}
            </Box>
            {(vendorBreakdownMerged || []).length > 8 && (
              <Typography level="body-xs" sx={{ opacity: 0.75, mt: 0.5 }}>
                Chart shows top 8 vendors; scroll the list for all vendors.
              </Typography>
            )}
          </Card>

          <Card variant="soft" sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography level="h3" sx={{ mb: 1 }}>
              Vendor breakdown (Table)
            </Typography>
            <Box
              sx={{
                maxHeight: 360,
                overflowY: 'auto',
                overflowX: 'hidden',
                flex: 1,
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0,0,0,0.25) transparent',
                '&::-webkit-scrollbar': { width: 8 },
                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.22)',
                  borderRadius: 4
                }
              }}
            >
              <Table
                size="sm"
                stripe="odd"
                hoverRow
                stickyHeader
                sx={{ '& thead th': { backgroundColor: 'background.level1' } }}
              >
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(vendorBreakdownMerged || []).map((r) => (
                    <tr key={r.vendor_id ?? r.vendor_name}>
                      <td>{r.vendor_name}</td>
                      <td style={{ textAlign: 'right' }}>{fmtMoney(r.totalAmount)}</td>
                    </tr>
                  ))}
                  {(vendorBreakdownMerged || []).length === 0 && (
                    <tr>
                      <td colSpan={2} style={{ opacity: 0.7 }}>
                        No purchases found for this range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Box>
            {(vendorBreakdownMerged || []).length > 10 && (
              <Typography level="body-xs" sx={{ opacity: 0.75, mt: 0.5 }}>
                Scroll to see all {vendorBreakdownMerged.length} vendors.
              </Typography>
            )}
          </Card>
        </Box>
      </Card>

      {/* Expiry */}
      {hasExpiryData && (
      <Card sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
          <Typography level="h2" sx={{ color: 'primary.500' }}>
            Expiry
          </Typography>
          {expiry?.counts && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {expiry.counts.expired > 0 && (
                <Chip color="danger" variant="soft">Expired: {expiry.counts.expired}</Chip>
              )}
              {expiry.counts.expiringSoon > 0 && (
                <Chip color="warning" variant="soft">Expiring soon: {expiry.counts.expiringSoon}</Chip>
              )}
              {expiry.counts.implantsExpired > 0 && (
                <Chip color="danger" variant="soft">Implants expired: {expiry.counts.implantsExpired}</Chip>
              )}
              {expiry.counts.implantsExpiringSoon > 0 && (
                <Chip color="warning" variant="soft">Implants expiring soon: {expiry.counts.implantsExpiringSoon}</Chip>
              )}
            </Box>
          )}
        </Box>

        {((expiry?.expiringSoon || []).length > 0 || (expiry?.expired || []).length > 0) && (
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {(expiry?.expiringSoon || []).length > 0 && (
          <Card variant="soft" sx={{ p: 2 }}>
            <Typography level="h3" sx={{ mb: 1 }}>
              Materials expiring soon
            </Typography>
            <ResponsiveDetailTable
              mobile={(
                <ReportMobileRowList
                  rows={(expiry?.expiringSoon || []).slice(0, 10)}
                  emptyMessage="No batches expiring in this window."
                  getRowKey={(m) => m.id}
                  columns={[
                    { label: 'Material', width: 'minmax(72px, 1.3fr)', emphasize: true, render: (m) => m.material_name },
                    { label: 'Vendor', width: 'minmax(64px, 1fr)', render: (m) => m.vendor_name || '—' },
                    { label: 'Qty', width: '32px', align: 'right', render: (m) => m.quantity },
                    { label: 'Days', width: '36px', align: 'right', render: (m) => daysToExpire(m.expiry_date, expiry?.asOf) },
                    { label: 'Expiry', width: 'minmax(72px, 0.9fr)', render: (m) => fmtDateShort(m.expiry_date) }
                  ]}
                />
              )}
            >
              <Table size="sm" stripe="odd" hoverRow>
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Vendor</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Days to expire</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {(expiry?.expiringSoon || []).slice(0, 10).map((m) => (
                    <tr key={m.id}>
                      <td>{m.material_name}</td>
                      <td>{m.vendor_name || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{m.quantity}</td>
                      <td style={{ textAlign: 'right' }}>{daysToExpire(m.expiry_date, expiry?.asOf)}</td>
                      <td>{fmtDateTime(m.expiry_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </Card>
          )}

          {(expiry?.expired || []).length > 0 && (
          <Card variant="soft" sx={{ p: 2 }}>
            <Typography level="h3" sx={{ mb: 0.5 }}>
              Materials expired
            </Typography>
            <Typography level="body-xs" sx={{ mb: 1, opacity: 0.75 }}>
              Cannot be consumed. Use Return or Disposed from the material batch list.
            </Typography>
            <ResponsiveDetailTable
              mobile={(
                <ReportMobileRowList
                  rows={(expiry?.expired || []).slice(0, 10)}
                  emptyMessage="No expired batches (excluding noted/deleted)."
                  getRowKey={(m) => m.id}
                  columns={[
                    { label: 'Material', width: 'minmax(80px, 1.4fr)', emphasize: true, render: (m) => m.material_name },
                    { label: 'Vendor', width: 'minmax(64px, 1fr)', render: (m) => m.vendor_name || '—' },
                    { label: 'Qty', width: '32px', align: 'right', render: (m) => m.quantity },
                    { label: 'Expiry', width: 'minmax(72px, 0.9fr)', render: (m) => fmtDateShort(m.expiry_date) }
                  ]}
                />
              )}
            >
              <Table size="sm" stripe="odd" hoverRow>
                <thead>
                  <tr>
                    <th>Material</th>
                    <th>Vendor</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {(expiry?.expired || []).slice(0, 10).map((m) => (
                    <tr key={m.id}>
                      <td>{m.material_name}</td>
                      <td>{m.vendor_name || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{m.quantity}</td>
                      <td>{fmtDateTime(m.expiry_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </Card>
          )}
        </Box>
        )}

        {((expiry?.implantsExpiringSoon || []).length > 0 || (expiry?.implantsExpired || []).length > 0) && (
        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          {(expiry?.implantsExpiringSoon || []).length > 0 && (
          <Card variant="soft" sx={{ p: 2 }}>
            <Typography level="h3" sx={{ mb: 1 }}>
              Implants expiring soon
            </Typography>
            <ResponsiveDetailTable
              mobile={(
                <ReportMobileRowList
                  rows={(expiry?.implantsExpiringSoon || []).slice(0, 10)}
                  emptyMessage="No implants expiring in this window."
                  getRowKey={(i) => i.id}
                  columns={[
                    { label: 'Implant', width: 'minmax(72px, 1.3fr)', emphasize: true, render: (i) => i.implant_name },
                    { label: 'Vendor', width: 'minmax(64px, 1fr)', render: (i) => i.vendor_name || '—' },
                    { label: 'Qty', width: '32px', align: 'right', render: (i) => i.quantity },
                    { label: 'Days', width: '36px', align: 'right', render: (i) => daysToExpire(i.expiry_date, expiry?.asOf) },
                    { label: 'Expiry', width: 'minmax(72px, 0.9fr)', render: (i) => fmtDateShort(i.expiry_date) }
                  ]}
                />
              )}
            >
              <Table size="sm" stripe="odd" hoverRow>
                <thead>
                  <tr>
                    <th>Implant</th>
                    <th>Vendor</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Days to expire</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {(expiry?.implantsExpiringSoon || []).slice(0, 10).map((i) => (
                    <tr key={i.id}>
                      <td>{i.implant_name}</td>
                      <td>{i.vendor_name || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{i.quantity}</td>
                      <td style={{ textAlign: 'right' }}>{daysToExpire(i.expiry_date, expiry?.asOf)}</td>
                      <td>{fmtDateTime(i.expiry_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </Card>
          )}

          {(expiry?.implantsExpired || []).length > 0 && (
          <Card variant="soft" sx={{ p: 2 }}>
            <Typography level="h3" sx={{ mb: 0.5 }}>
              Implants expired
            </Typography>
            <Typography level="body-xs" sx={{ mb: 1, opacity: 0.75 }}>
              Cannot be consumed. Use Return or Disposed from the implant batch list.
            </Typography>
            <ResponsiveDetailTable
              mobile={(
                <ReportMobileRowList
                  rows={(expiry?.implantsExpired || []).slice(0, 10)}
                  emptyMessage="No expired implants (with qty > 0)."
                  getRowKey={(i) => i.id}
                  columns={[
                    { label: 'Implant', width: 'minmax(80px, 1.4fr)', emphasize: true, render: (i) => i.implant_name },
                    { label: 'Vendor', width: 'minmax(64px, 1fr)', render: (i) => i.vendor_name || '—' },
                    { label: 'Qty', width: '32px', align: 'right', render: (i) => i.quantity },
                    { label: 'Expiry', width: 'minmax(72px, 0.9fr)', render: (i) => fmtDateShort(i.expiry_date) }
                  ]}
                />
              )}
            >
              <Table size="sm" stripe="odd" hoverRow>
                <thead>
                  <tr>
                    <th>Implant</th>
                    <th>Vendor</th>
                    <th style={{ textAlign: 'right' }}>Qty</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {(expiry?.implantsExpired || []).slice(0, 10).map((i) => (
                    <tr key={i.id}>
                      <td>{i.implant_name}</td>
                      <td>{i.vendor_name || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{i.quantity}</td>
                      <td>{fmtDateTime(i.expiry_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ResponsiveDetailTable>
          </Card>
          )}
        </Box>
        )}
      </Card>
      )}

      <Modal open={srDrillOpen} onClose={() => setSrDrillOpen(false)}>
        <ModalDialog sx={{ width: { xs: '95vw', md: 860 }, maxHeight: '85vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h2" sx={{ color: 'primary.500' }}>
            Returns &amp; disposals drilldown
          </Typography>
          <Typography level="body-sm" sx={{ mt: 0.5, opacity: 0.8 }}>
            Period: <strong>{srDrillPeriod || '—'}</strong> ({group})
          </Typography>

          {srDrillError && (
            <Alert color="danger" sx={{ mt: 2 }}>
              {srDrillError}
            </Alert>
          )}

          {srDrillLoading && (
            <Typography level="body-sm" sx={{ mt: 2 }}>
              Loading…
            </Typography>
          )}

          {!srDrillLoading && srDrillData && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip variant="soft">Materials returned: {fmtMoney(srDrillData.totals?.materialsReturned)}</Chip>
                <Chip variant="soft">Materials disposed: {fmtMoney(srDrillData.totals?.materialsDisposed)}</Chip>
                <Chip variant="soft">Implants returned: {fmtMoney(srDrillData.totals?.implantsReturned)}</Chip>
                <Chip variant="soft">Implants disposed: {fmtMoney(srDrillData.totals?.implantsDisposed)}</Chip>
              </Box>

              <Tabs value={srDrillTab} onChange={(_, v) => setSrDrillTab(v)}>
                <TabList>
                  <Tab value="materials">Materials</Tab>
                  <Tab value="implants">Implants</Tab>
                </TabList>

                {(['materials', 'implants'] || []).map((k) => (
                  <TabPanel key={k} value={k} sx={{ px: 0 }}>
                    <Table size="sm" stripe="odd" hoverRow sx={{ mt: 1 }}>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th style={{ textAlign: 'right' }}>Returned</th>
                          <th style={{ textAlign: 'right' }}>Disposed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(srDrillData.items?.[k] || []).map((r) => (
                          <tr key={r.item_name}>
                            <td>{r.item_name}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoney(r.returned)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoney(r.disposed)}</td>
                          </tr>
                        ))}
                        {(srDrillData.items?.[k] || []).length === 0 && (
                          <tr>
                            <td colSpan={3} style={{ opacity: 0.7 }}>
                              No returns or disposals for this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </TabPanel>
                ))}
              </Tabs>
            </Box>
          )}
        </ModalDialog>
      </Modal>

      <Modal open={drillOpen} onClose={() => setDrillOpen(false)}>
        <ModalDialog sx={{ width: { xs: '95vw', md: 860 }, maxHeight: '85vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h2" sx={{ color: 'primary.500' }}>
            Consumption drilldown
          </Typography>
          <Typography level="body-sm" sx={{ mt: 0.5, opacity: 0.8 }}>
            Period: <strong>{drillPeriod || '—'}</strong> ({group})
          </Typography>

          {drillError && (
            <Alert color="danger" sx={{ mt: 2 }}>
              {drillError}
            </Alert>
          )}

          {drillLoading && (
            <Typography level="body-sm" sx={{ mt: 2 }}>
              Loading…
            </Typography>
          )}

          {!drillLoading && drillData && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip variant="soft">Materials: {fmtMoney(drillData.totals?.materials)}</Chip>
                <Chip variant="soft">Disposables: {fmtMoney(drillData.totals?.disposables)}</Chip>
                <Chip variant="soft">Implants: {fmtMoney(drillData.totals?.implants)}</Chip>
              </Box>

              <Tabs value={drillTab} onChange={(_, v) => setDrillTab(v)}>
                <TabList>
                  <Tab value="materials">Materials</Tab>
                  <Tab value="disposables">Disposables</Tab>
                  <Tab value="implants">Implants</Tab>
                </TabList>

                {(['materials', 'disposables', 'implants'] || []).map((k) => (
                  <TabPanel key={k} value={k} sx={{ px: 0 }}>
                    <Table size="sm" stripe="odd" hoverRow sx={{ mt: 1 }}>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th style={{ textAlign: 'right' }}>Consumed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(drillData.items?.[k] || []).map((r) => (
                          <tr key={r.item_name}>
                            <td>{r.item_name}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoney(r.qty)}</td>
                          </tr>
                        ))}
                        {(drillData.items?.[k] || []).length === 0 && (
                          <tr>
                            <td colSpan={2} style={{ opacity: 0.7 }}>
                              No consumption rows for this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </TabPanel>
                ))}
              </Tabs>
            </Box>
          )}
        </ModalDialog>
      </Modal>

      <Modal open={pDrillOpen} onClose={() => setPDrillOpen(false)}>
        <ModalDialog sx={{ width: { xs: '95vw', md: 920 }, maxHeight: '85vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h2" sx={{ color: 'primary.500' }}>
            Purchases drilldown
          </Typography>
          <Typography level="body-sm" sx={{ mt: 0.5, opacity: 0.8 }}>
            Period: <strong>{pDrillPeriod || '—'}</strong> ({group})
            {vendorId ? (
              <>
                {' '}— Vendor filter: <strong>{vendorNameById.get(vendorId) || vendorId}</strong>
              </>
            ) : null}
          </Typography>

          {pDrillError && (
            <Alert color="danger" sx={{ mt: 2 }}>
              {pDrillError}
            </Alert>
          )}

          {pDrillLoading && (
            <Typography level="body-sm" sx={{ mt: 2 }}>
              Loading…
            </Typography>
          )}

          {!pDrillLoading && pDrillData && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip color="warning" variant="soft">
                  Total amount: {fmtMoney((pDrillData.totals?.materialsAmount || 0) + (pDrillData.totals?.disposablesAmount || 0) + (pDrillData.totals?.implantsAmount || 0))}
                </Chip>
                <Chip variant="soft">
                  Total qty: {fmtMoney((pDrillData.totals?.materialsQty || 0) + (pDrillData.totals?.disposablesQty || 0) + (pDrillData.totals?.implantsQty || 0))}
                </Chip>
              </Box>

              <Tabs value={pDrillTab} onChange={(_, v) => setPDrillTab(v)}>
                <TabList>
                  <Tab value="materials">
                    Materials ({fmtMoney(pDrillData.totals?.materialsQty)} / {fmtMoney(pDrillData.totals?.materialsAmount)})
                  </Tab>
                  <Tab value="disposables">
                    Disposables ({fmtMoney(pDrillData.totals?.disposablesQty)} / {fmtMoney(pDrillData.totals?.disposablesAmount)})
                  </Tab>
                  <Tab value="implants">
                    Implants ({fmtMoney(pDrillData.totals?.implantsQty)} / {fmtMoney(pDrillData.totals?.implantsAmount)})
                  </Tab>
                  <Tab value="vendors">
                    Vendors ({fmtMoney((pDrillData.vendors || []).reduce((a, r) => a + Number(r.qty || 0), 0))} /{' '}
                    {fmtMoney((pDrillData.vendors || []).reduce((a, r) => a + Number(r.amount || 0), 0))})
                  </Tab>
                </TabList>

                {(['materials', 'disposables', 'implants'] || []).map((k) => (
                  <TabPanel key={k} value={k} sx={{ px: 0 }}>
                    <Table size="sm" stripe="odd" hoverRow sx={{ mt: 1 }}>
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th style={{ textAlign: 'right' }}>Qty</th>
                          <th style={{ textAlign: 'right' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(pDrillData.items?.[k] || []).map((r) => (
                          <tr key={r.item_name}>
                            <td>{r.item_name}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoney(r.qty)}</td>
                            <td style={{ textAlign: 'right' }}>{fmtMoney(r.amount)}</td>
                          </tr>
                        ))}
                        {(pDrillData.items?.[k] || []).length === 0 && (
                          <tr>
                            <td colSpan={3} style={{ opacity: 0.7 }}>
                              No purchase rows for this period.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </TabPanel>
                ))}

                <TabPanel value="vendors" sx={{ px: 0 }}>
                  <Table size="sm" stripe="odd" hoverRow sx={{ mt: 1 }}>
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th style={{ textAlign: 'right' }}>Qty</th>
                        <th style={{ textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(pDrillData.vendors || []).map((r) => (
                        <tr key={`${r.vendor_id}-${r.vendor_name}`}>
                          <td>{r.vendor_name}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoney(r.qty)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtMoney(r.amount)}</td>
                        </tr>
                      ))}
                      {(pDrillData.vendors || []).length === 0 && (
                        <tr>
                          <td colSpan={3} style={{ opacity: 0.7 }}>
                            No vendor rows for this period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </TabPanel>
              </Tabs>
            </Box>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default Reports;

